import { useState } from "react";
import { Flag, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useSafety } from "@/lib/safety/useSafety";
import { REPORT_REASON_LABELS, type ReportReason } from "@/lib/safety/types";
import { toast } from "sonner";

interface Props {
  targetUserId: string;
  messageId?: string;
  trigger?: React.ReactNode;
  onReported?: () => void;
}

const ReportDialog = ({ targetUserId, messageId, trigger, onReported }: Props) => {
  const { reportUser } = useSafety();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<ReportReason>("harassment");
  const [context, setContext] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    const { error } = await reportUser(targetUserId, reason, context.trim() || undefined, messageId);
    setBusy(false);
    if (error) {
      toast.error(error);
      return;
    }
    toast.success("Report submitted. Our safety team will review it.");
    setOpen(false);
    setContext("");
    onReported?.();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm">
            <Flag className="h-4 w-4 mr-1" /> Report
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Report this {messageId ? "message" : "profile"}</DialogTitle>
          <DialogDescription>
            Our safety team reviews every report. Reports are confidential — the person
            you're reporting won't see who flagged them.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium">Reason</Label>
            <RadioGroup
              value={reason}
              onValueChange={(v) => setReason(v as ReportReason)}
              className="mt-2 grid gap-2"
            >
              {(Object.keys(REPORT_REASON_LABELS) as ReportReason[]).map((r) => (
                <label
                  key={r}
                  className="flex items-center gap-3 rounded-lg border border-border p-3 cursor-pointer hover:bg-muted/50"
                >
                  <RadioGroupItem value={r} id={`reason-${r}`} />
                  <span className="text-sm">{REPORT_REASON_LABELS[r]}</span>
                </label>
              ))}
            </RadioGroup>
          </div>

          <div>
            <Label htmlFor="context" className="text-sm font-medium">
              Additional context (optional)
            </Label>
            <Textarea
              id="context"
              rows={3}
              maxLength={500}
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Anything else our team should know?"
              className="mt-2"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy} variant="destructive">
            {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Submit report
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReportDialog;
